import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCw, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type SharePointSite } from "@noa/api-core";
import { useAllSchemas, useTriggerIngestion } from "@noa/api-client";
import { SharePointSyncConfigForm } from "./sharepoint-sync-config-form";
import {
  sharePointSyncDefaultValues,
  sharePointSyncSchema,
  type SharePointSyncForm,
} from "~/utils/sharepoint-sync-form";
import { Alert, Button, Card, message, Switch, Text, Title } from "~/components/ui";

type SharePointSyncPanelProps = {
  onNavigateToHistory?: () => void;
  /** The form to start from, e.g. the settings of a sync to run again. */
  initialValues?: SharePointSyncForm;
  /** The job whose settings fill the form, when it runs a sync again. */
  relaunchedFrom?: string;
};

export function SharePointSyncPanel({ onNavigateToHistory, initialValues, relaunchedFrom }: SharePointSyncPanelProps) {
  const { t } = useTranslation("admin");
  const [lastJobId, setLastJobId] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string; alreadyRunning: boolean } | null>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);

  // A relaunch lands here from another tab: the next step is to start, so focus goes there.
  useEffect(() => {
    if (relaunchedFrom) startButtonRef.current?.focus();
  }, [relaunchedFrom]);

  const { control, handleSubmit, watch } = useForm<SharePointSyncForm>({
    resolver: zodResolver(sharePointSyncSchema),
    defaultValues: initialValues ?? sharePointSyncDefaultValues,
  });
  const withVision = watch("withVision");

  const { schemas, isLoading: schemasLoading } = useAllSchemas();

  const triggerSync = useTriggerIngestion();

  const handleSync = (values: SharePointSyncForm) => {
    triggerSync.mutate(
      {
        files: ["*"],
        source_type: "sharepoint",
        schema_id: values.schemaId || undefined,
        parser: values.parser || undefined,
        with_vision: values.withVision,
        skip_existing: values.skipExisting,
        site: (values.site as SharePointSite | null | undefined) || undefined,
        max_records: values.limitEnabled ? values.maxRecords : undefined,
      },
      {
        onSuccess: (data) => {
          setLastJobId(data.job_id);
          setError(null);
          void message.success(t("uploadAdmin.sync.sharepoint.started"));
        },
        onError: (err) => {
          // 409: the one-sync-at-a-time guard refused it, which the generic message hid.
          const alreadyRunning = err.status === 409;
          setError({
            message: t(
              alreadyRunning ? "uploadAdmin.sync.sharepoint.alreadyRunning" : "uploadAdmin.sync.sharepoint.error"
            ),
            alreadyRunning,
          });
        },
      }
    );
  };

  const handleInvalid = () => {
    void message.error(t("uploadAdmin.sync.invalidForm"));
  };

  const schemaOptions = schemas.map((s) => ({
    value: s.schema_id,
    label: `${s.schema_id} — ${s.description || s.doc_type}`,
  }));
  const incrementalSchemaIds = new Set(schemas.filter((s) => s.incremental_sync).map((s) => s.schema_id));

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Share2 size={30} className="text-green-500" />
        <div>
          <Title level={4} className="!mb-0">
            {t("uploadAdmin.sync.sharepoint.title")}
          </Title>
          <Text tone="muted">{t("uploadAdmin.sync.sharepoint.description")}</Text>
        </div>
      </div>

      {relaunchedFrom && !lastJobId && (
        <Alert
          type="info"
          message={t("uploadAdmin.sync.sharepoint.relaunchNotice", { jobId: relaunchedFrom.slice(0, 8) })}
          showIcon
        />
      )}

      {/* Sync Action Card */}
      <Card size="small" className="bg-noa-hover">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1">
            <Title level={5} className="!mb-1">
              {t("uploadAdmin.sync.sharepoint.syncTitle")}
            </Title>
            <Text tone="muted">{t("uploadAdmin.sync.sharepoint.syncDescription")}</Text>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Text className="text-xs">{t("uploadAdmin.form.withVision")}</Text>
                <Controller
                  name="withVision"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      size="small"
                      checked={field.value}
                      onChange={field.onChange}
                      aria-label={t("uploadAdmin.form.withVision")}
                    />
                  )}
                />
              </div>
              {!withVision && (
                <Text className="text-xs text-orange-600 text-right max-w-48">
                  ⚠️ {t("uploadAdmin.sync.maximo.visionWarning")}
                </Text>
              )}
            </div>
            <Button
              variant="primary"
              size="large"
              icon={<RefreshCw size={14} className={triggerSync.isPending ? "animate-spin" : undefined} />}
              loading={triggerSync.isPending}
              onClick={handleSubmit(handleSync, handleInvalid)}
              ref={startButtonRef}
              data-testid="sharepoint-sync-button"
            >
              {t("uploadAdmin.sync.sharepoint.startSync")}
            </Button>
          </div>
        </div>
      </Card>

      <SharePointSyncConfigForm
        control={control}
        schemaOptions={schemaOptions}
        schemasLoading={schemasLoading}
        incrementalSchemaIds={incrementalSchemaIds}
      />

      {/* Error message */}
      {error && (
        <Alert
          type="error"
          message={t("common:error")}
          description={error.message}
          action={
            error.alreadyRunning && onNavigateToHistory ? (
              <Button size="small" onClick={onNavigateToHistory}>
                {t("uploadAdmin.sync.viewInHistory")}
              </Button>
            ) : undefined
          }
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Success message */}
      {lastJobId && (
        <Alert
          type="success"
          message={t("uploadAdmin.sync.taskStarted")}
          description={
            <div className="inline-flex flex-col gap-2">
              <Text>
                {t("uploadAdmin.sync.jobId")}: <code className="bg-noa-hover px-2 py-1 rounded">{lastJobId}</code>
              </Text>
              {onNavigateToHistory && (
                <Button variant="link" size="small" className="!p-0 !h-auto" onClick={onNavigateToHistory}>
                  {t("uploadAdmin.sync.viewInHistory")} →
                </Button>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setLastJobId(null)}
        />
      )}

      {/* How it works */}
      <Card title={t("uploadAdmin.sync.sharepoint.howItWorks")} size="small">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 list-decimal pl-5 text-noa-muted">
          <li>{t("uploadAdmin.sync.sharepoint.step1")}</li>
          <li>{t("uploadAdmin.sync.sharepoint.step2")}</li>
          <li>{t("uploadAdmin.sync.sharepoint.step3")}</li>
          <li>{t("uploadAdmin.sync.sharepoint.step4")}</li>
        </ol>
      </Card>
    </div>
  );
}
